import { useEffect, useMemo, useState } from "react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { inputClass } from "../components/FormField";
import { fetchProformaRefactions, type ProformaRefaction } from "../api/proformaApi";

function money(value: number): string {
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Réfactions des demandes de validation : actes approuvés pour un montant inférieur à celui demandé.
 */
export function RefactionsPage() {
  const [rows, setRows] = useState<ProformaRefaction[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProformaRefactions()
      .then(setRows)
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => `${row.request_number} ${row.act} ${row.patient} ${row.package} ${row.company} ${row.hospital}`.toLowerCase().includes(term));
  }, [rows, search]);

  const totals = filtered.reduce(
    (sum, row) => ({ billed: sum.billed + row.billed, retained: sum.retained + row.retained }),
    { billed: 0, retained: 0 },
  );

  return (
    <>
      <Breadcrumbs items={[{ label: "Demandes de validation", href: "/dashboard/requests" }, { label: "Liste des réfactions" }]} />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Liste des réfactions</h1>
          <p className="text-sm text-muted">Actes des demandes approuvées pour un montant inférieur à celui demandé.</p>
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Acte, patient, entreprise…" className={`${inputClass} sm:max-w-xs`} />
      </div>
      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <div className="theme-card overflow-x-auto">
        <table className="safecheck-table w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-border bg-card-muted/50 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
              <th className="px-4 py-3">Acte</th>
              <th className="px-4 py-3">Patient</th>
              <th className="px-4 py-3">Package</th>
              <th className="px-4 py-3">Entreprise</th>
              <th className="px-4 py-3 text-right">Montant facturé</th>
              <th className="px-4 py-3 text-right">Montant refait</th>
              <th className="px-4 py-3 text-right">Réfaction</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-muted">Chargement…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-muted">Aucune réfaction.</td></tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{row.act}</p>
                    <p className="text-xs text-muted">Demande {row.request_number} · {row.hospital}</p>
                  </td>
                  <td className="px-4 py-3 text-foreground">{row.patient}</td>
                  <td className="px-4 py-3 text-foreground">{row.package || "—"}</td>
                  <td className="px-4 py-3 text-foreground">{row.company}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">{money(row.billed)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">{money(row.retained)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-red-700">{money(row.billed - row.retained)}</td>
                </tr>
              ))
            )}
          </tbody>
          {filtered.length > 0 ? (
            <tfoot>
              <tr className="border-t border-border bg-card-muted/40 font-semibold text-foreground">
                <td className="px-4 py-3" colSpan={4}>Total ({filtered.length} acte(s))</td>
                <td className="px-4 py-3 text-right tabular-nums">{money(totals.billed)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{money(totals.retained)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-red-700">{money(totals.billed - totals.retained)}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </>
  );
}
